/**
 * Forecasting Settings Tab
 *
 * Contains:
 *  - Forecast method configuration (Moving Average, Exponential Smoothing, ARIMA, Ensemble)
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField,
  Alert, CircularProgress, Snackbar,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Save, Settings } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ForecastMethodConfig {
  method: string;
  window_size?: number;
  smoothing_factor?: number;
  auto_tune?: boolean;
}

const FORECAST_METHODS = [
  { value: 'moving_average', label: 'Moving Average', description: 'Simple rolling average of recent periods' },
  { value: 'exponential_smoothing', label: 'Exponential Smoothing', description: 'Weighted average giving more weight to recent data' },
  { value: 'arima', label: 'ARIMA', description: 'Auto-Regressive Integrated Moving Average' },
  { value: 'ensemble', label: 'Ensemble (Recommended)', description: 'Combines multiple methods for best accuracy' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const ForecastingSettingsTab: React.FC = () => {
  const queryClient = useQueryClient();

  /* Forecast method state */
  const [methodConfig, setMethodConfig] = useState<ForecastMethodConfig>({
    method: 'ensemble',
    window_size: 12,
    smoothing_factor: 0.3,
    auto_tune: true,
  });

  /* Last successfully-saved snapshot — used to decide whether the Save
     button is "frozen" (no pending changes) or active. */
  const [savedConfig, setSavedConfig] = useState<ForecastMethodConfig | null>(null);

  /* UI state */
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  /* Load settings from backend */
  const { data: savedSettings, isLoading } = useQuery({
    queryKey: ['forecasting-settings'],
    queryFn: async () => {
      try {
        const response = await api.getSettings('forecasting');
        return response.data;
      } catch {
        return null;
      }
    },
  });

  /* Populate from saved data */
  useEffect(() => {
    if (savedSettings?.config) {
      setMethodConfig(savedSettings.config);
      setSavedConfig(savedSettings.config);
    }
  }, [savedSettings]);

  /* Are there unsaved changes? Compare the editable state against the last
     successful save. If equal, the Save button is frozen (disabled). */
  const hasUnsavedChanges = React.useMemo(() => {
    if (!savedConfig) return true; // never saved yet — allow first save
    return JSON.stringify(methodConfig) !== JSON.stringify(savedConfig);
  }, [methodConfig, savedConfig]);

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      await api.saveSettings('forecasting', { config: methodConfig });
      // Rebase the "clean" snapshot onto what we just saved so the Save
      // button disables itself until the user makes a new edit.
      setSavedConfig(methodConfig);
      queryClient.invalidateQueries({ queryKey: ['forecasting-settings'] });
      setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Save failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Forecasting Settings
          </Typography>
          <Button
            variant="contained" color="success"
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSaveAll}
            disabled={isSaving || !hasUnsavedChanges}
          >
            {hasUnsavedChanges ? 'Save All Settings' : 'Saved'}
          </Button>
        </Box>

        {/* Forecast Method Configuration */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings sx={{ fontSize: 18 }} />
            Forecast Method
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Forecast Method</InputLabel>
                <Select
                  value={methodConfig.method}
                  label="Forecast Method"
                  onChange={(e) => setMethodConfig((prev) => ({ ...prev, method: e.target.value }))}
                >
                  {FORECAST_METHODS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      <Box>
                        <Typography sx={{ fontSize: '13px' }}>{m.label}</Typography>
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{m.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {(methodConfig.method === 'moving_average' || methodConfig.method === 'ensemble') && (
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth size="small" type="number"
                  label="Window Size (months)"
                  value={methodConfig.window_size || 12}
                  onChange={(e) => setMethodConfig((prev) => ({ ...prev, window_size: parseInt(e.target.value) || 12 }))}
                  inputProps={{ min: 3, max: 24 }}
                />
              </Grid>
            )}

            {(methodConfig.method === 'exponential_smoothing' || methodConfig.method === 'ensemble') && (
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth size="small" type="number"
                  label="Smoothing Factor (alpha)"
                  value={methodConfig.smoothing_factor || 0.3}
                  onChange={(e) => setMethodConfig((prev) => ({ ...prev, smoothing_factor: parseFloat(e.target.value) || 0.3 }))}
                  inputProps={{ min: 0.01, max: 1, step: 0.05 }}
                />
              </Grid>
            )}
          </Grid>

          <Alert severity="info" sx={{ mt: 2, fontSize: '12px' }}>
            The selected forecast method will be applied to all new forecast generations.
            Existing forecasts are not retroactively recalculated.
          </Alert>
        </Paper>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default ForecastingSettingsTab;
